import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { fetchPackages } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Packages() {
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    fetchPackages().then(setPackages).catch(() => {});
  }, []);

  return (
    <div data-testid="packages-page" className="pt-32 md:pt-40">
      <section className="mx-auto max-w-[1400px] px-6 md:px-12">
        <span className="overline text-primary">Curated Journeys</span>
        <h1 className="mt-6 max-w-4xl font-serif text-5xl leading-[1.02] tracking-tight md:text-7xl lg:text-8xl">
          Three portraits of a <em className="italic text-primary">sacred</em> passage.
        </h1>
        <p className="mt-10 max-w-2xl font-sans-lux text-lg leading-relaxed text-foreground/70">
          Each journey is composed for a particular intent — the sovereign, the devoted, the contemplative.
          Every element is bespoke; the itineraries below are starting points, not scripts.
        </p>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-24 md:px-12 md:py-32">
        <div className="space-y-14">
          {packages.map((p, i) => (
            <article
              key={p.id}
              data-testid={`package-row-${p.id}`}
              className="group grid gap-8 border hairline bg-secondary/30 p-6 transition-colors hover:border-primary/50 md:grid-cols-12 md:gap-14 md:p-10"
            >
              <div className={`relative aspect-[4/3] overflow-hidden md:col-span-5 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                <img src={p.hero_image} alt={p.name} className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
              </div>
              <div className="md:col-span-7">
                <span className="overline text-primary">{p.tagline}</span>
                <h2 className="mt-4 font-serif text-4xl leading-none md:text-6xl">{p.name}</h2>
                <div className="mt-4 flex flex-wrap items-baseline gap-6">
                  <span className="overline text-foreground/60">{p.duration}</span>
                  <span className="font-serif text-2xl text-primary">
                    from ₹{p.price_from.toLocaleString("en-IN")}
                  </span>
                </div>
                <p className="mt-8 max-w-2xl font-sans-lux text-lg leading-relaxed text-foreground/75">
                  {p.description}
                </p>

                <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                  {p.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-3 font-sans-lux text-sm text-foreground/80">
                      <Check className="mt-1 shrink-0 text-primary" size={16} strokeWidth={1.5} />
                      {h}
                    </li>
                  ))}
                </ul>

                <div className="mt-10 flex flex-wrap items-center gap-4">
                  <Link to={`/packages/${p.id}`}>
                    <Button
                      data-testid={`package-detail-${p.id}`}
                      className="rounded-none bg-primary px-8 py-6 text-primary-foreground hover:bg-primary/90"
                    >
                      <span className="overline">View Details</span>
                      <ArrowUpRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  </Link>
                  <Link to={`/plan-your-visit?package=${p.id}`}>
                    <Button
                      data-testid={`package-inquire-${p.id}`}
                      variant="ghost"
                      className="rounded-none border hairline px-8 py-6 text-foreground hover:bg-white/5"
                    >
                      <span className="overline">Inquire</span>
                    </Button>
                  </Link>
                  <span className="overline text-foreground/50">Best for {p.best_for}</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
